import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './dialog'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './tabs'

/**
 * ModalLayout - Standardized reusable modal component
 *
 * Provides consistent structure across all modals:
 * - Header with optional icon and title
 * - Optional tabs system with multiple content areas
 * - Scrollable body content
 * - Optional footer with action buttons
 * - Uses design tokens for consistent styling
 * - Built on shadcn/ui primitives (Dialog, Tabs, ScrollArea)
 *
 * Props:
 * - isOpen: Boolean - Control modal visibility
 * - onOpenChange: Function - Called when modal should open/close
 * - title: String - Modal header title
 * - icon: ReactNode - Optional icon to display in header (emoji or icon component)
 * - tabs: Array<{value, label, content, icon?}> - Optional tab definition
 * - activeTab: String - Currently active tab value
 * - onTabChange: Function - Called when tab changes
 * - children: ReactNode - Content to display (if no tabs)
 * - footerContent: ReactNode - Optional footer with buttons
 * - maxWidth: String - Max width class (default: 'md')
 * - headerImage: String - Optional header background image URL
 * - onClose: Function - Alias for onOpenChange(false)
 */
export function ModalLayout({
  isOpen,
  onOpenChange,
  title,
  icon,
  tabs,
  activeTab,
  onTabChange,
  children,
  footerContent,
  maxWidth = 'md',
  headerImage,
  onClose,
  /** Radix onPointerDownOutside passthrough (e.g. suppress dismissal while an overlay is open) */
  onPointerDownOutside,
}) {
  const maxWidthClass = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    'screen-2xl': 'max-w-screen-2xl',
  }[maxWidth] || maxWidth

  // Support both onOpenChange and onClose
  const handleOpenChange = (open) => {
    if (onOpenChange) {
      onOpenChange(open)
    } else if (!open && onClose) {
      onClose()
    }
  }

  const hasHeaderImage = headerImage && headerImage.trim()
  const hasTabs = tabs && tabs.length > 0

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={`${maxWidthClass} max-h-[90vh] flex flex-col gap-0 p-0 border border-border-primary bg-bg-secondary overflow-hidden`}
        aria-describedby={undefined}
        {...(onPointerDownOutside ? { onPointerDownOutside } : {})}
      >
        {/* Header with optional background image */}
        {hasHeaderImage && (
          <div
            className="h-40 bg-cover bg-center relative"
            style={{
              backgroundImage: `url(${headerImage})`,
              backgroundPosition: 'center',
            }}
          >
            {/* Dark overlay for readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-bg-secondary/80"></div>
          </div>
        )}

        {/* Header with title and icon */}
        <DialogHeader
          className={`flex flex-row items-center justify-between px-6 ${hasHeaderImage ? 'py-4 -mt-12 relative z-10' : 'py-4 border-b border-border-primary'
            }`}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {icon && (
              <span className="text-2xl flex-shrink-0" role="img" aria-hidden="true">
                {icon}
              </span>
            )}
            <DialogTitle className="text-xl font-semibold text-text-primary truncate">
              {title}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Content area */}
        {hasTabs ? (
          <Tabs
            value={activeTab}
            onValueChange={onTabChange}
            className="flex flex-col flex-1 min-h-0"
          >
            {/* Tab list */}
            <div className="px-6 pt-4 border-b border-border-primary flex-shrink-0">
              <TabsList className="w-full justify-start gap-2 bg-transparent border-b border-transparent h-auto p-0">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="relative px-3 py-2 text-sm font-medium text-text-secondary data-[state=active]:text-text-primary rounded-none border-b-2 border-transparent data-[state=active]:border-rarity-rare transition-colors"
                  >
                    {tab.icon && (
                      <span className="mr-2" role="img" aria-hidden="true">
                        {tab.icon}
                      </span>
                    )}
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* Tab content - scrollable */}
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
              {tabs.map((tab) => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="mt-0 focus-visible:outline-none"
                >
                  <div className="px-6 py-4">
                    {tab.content}
                  </div>
                </TabsContent>
              ))}
            </div>
          </Tabs>
        ) : (
          /* Non-tabbed content - scrollable */
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 py-4">
            {children}
          </div>
        )}

        {/* Footer with action buttons */}
        {footerContent && (
          <div className="border-t border-border-primary px-4 py-4 sm:px-6 flex flex-wrap gap-2 justify-end items-center">
            {footerContent}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ModalLayout
